import React, { useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getAggregateVotes, getRadarData } from '../../utils/calculations';
import { api } from '../../api/client';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const VisualizationsTab = () => {
  const { activeSession, uncertainties, votes, setVotes } = useAppContext();

  const loadVotes = useCallback(async () => {
    if (!activeSession) return;
    try {
      const data = await api.getVotes(activeSession.id);
      setVotes(data);
    } catch (error) {
      console.error('Failed to load votes:', error);
    }
  }, [activeSession, setVotes]);

  useEffect(() => {
    loadVotes();
  }, [loadVotes]);

  const aggregateVotes = getAggregateVotes(uncertainties, votes);
  const radarData = getRadarData(uncertainties, votes);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold text-gray-900">{payload[0].payload.name}</p>
          <p className="text-sm text-gray-600">Impact: {payload[0].payload.impact}</p>
          <p className="text-sm text-gray-600">Uncertainty: {payload[0].payload.uncertainty}</p>
          <p className="text-xs text-gray-500 mt-1">{payload[0].payload.category}</p>
        </div>
      );
    }
    return null;
  };

  const topCritical = aggregateVotes
    .sort((a, b) => (b.impact * b.uncertainty) - (a.impact * a.uncertainty))
    .slice(0, 5);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Impact vs Uncertainty Matrix</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="uncertainty"
                name="Uncertainty"
                domain={[0, 10]}
                label={{ value: 'Uncertainty Level', position: 'bottom', offset: 40 }}
              />
              <YAxis
                type="number"
                dataKey="impact"
                name="Impact"
                domain={[0, 10]}
                label={{ value: 'Impact Level', angle: -90, position: 'left', offset: 40 }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
              <Scatter name="Uncertainties" data={aggregateVotes} fill="#6366f1" />
            </ScatterChart>
          </ResponsiveContainer>
          {aggregateVotes.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No voting data yet. Submit votes to see visualizations.
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Analysis Radar</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="category" />
              <PolarRadiusAxis domain={[0, 10]} />
              <Radar
                name="Average Impact"
                dataKey="value"
                stroke="#6366f1"
                fill="#6366f1"
                fillOpacity={0.6}
              />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Voting Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#e6e6f5] rounded-lg p-6">
            <div className="text-3xl font-bold text-[#01006A] mb-2">
              {uncertainties.length}
            </div>
            <div className="text-sm text-gray-600">Total Uncertainties</div>
          </div>
          <div className="bg-[#e6e6f5] rounded-lg p-6">
            <div className="text-3xl font-bold text-[#01006A] mb-2">
              {votes.length}
            </div>
            <div className="text-sm text-gray-600">Total Votes Cast</div>
          </div>
          <div className="bg-[#e6e6f5] rounded-lg p-6">
            <div className="text-3xl font-bold text-[#01006A] mb-2">
              {new Set(votes.map(v => v.user_id)).size}
            </div>
            <div className="text-sm text-gray-600">Participants</div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Critical Uncertainties</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-3">
            {topCritical.map((item, index) => (
              <div key={index} className="flex items-center justify-between bg-white p-4 rounded-lg">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{item.name}</div>
                  <div className="text-sm text-gray-600">{item.category}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-[#01006A]">
                    Score: {(item.impact * item.uncertainty).toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-500">
                    I:{item.impact} × U:{item.uncertainty}
                  </div>
                </div>
              </div>
            ))}
            {topCritical.length === 0 && (
              <div className="text-center text-gray-500 py-6">
                No data available yet
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualizationsTab;